"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

interface ImageItem {
  url: string;
  name: string;
}

interface ImageModalLabels {
  imageViewer?: string;
  previousImage?: string;
  nextImage?: string;
  closeImage?: string;
}

interface ImageModalProps {
  images: ImageItem[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  labels?: ImageModalLabels;
}

// swipe-down 으로 닫기 위한 최소 끌기 거리(px)
const SWIPE_CLOSE_THRESHOLD = 120;

// 닫기/카운터를 노치·상태바 아래로. native iOS 에서는 var 가 0 이라 1rem 과 동일.
const TOP_SAFE = "top-[max(1rem,var(--loc-safe-area-inset-top))]";

export function ImageModal({
  images,
  currentIndex,
  isOpen,
  onClose,
  onIndexChange,
  labels = {},
}: ImageModalProps) {
  const {
    imageViewer = "View image",
    previousImage = "Previous image",
    nextImage = "Next image",
    closeImage = "Close",
  } = labels;

  const hasMultipleImages = images.length > 1;

  // 핀치-줌 상태(scale > 1) 와 swipe-down 끌기 거리.
  // 줌 중에는 한 손가락 pan 이 우선, 줌이 없을 때만 swipe-down 닫기를 활성화한다.
  const [isZoomed, setIsZoomed] = useState(false);
  const [dragY, setDragY] = useState(0);
  const touchStartY = useRef<number | null>(null);

  const resetView = useCallback(() => {
    setIsZoomed(false);
    setDragY(0);
    touchStartY.current = null;
  }, []);

  const handleClose = useCallback(() => {
    resetView();
    onClose();
  }, [resetView, onClose]);

  const goToPrevious = useCallback(() => {
    resetView();
    onIndexChange(currentIndex > 0 ? currentIndex - 1 : images.length - 1);
  }, [currentIndex, images.length, onIndexChange, resetView]);

  const goToNext = useCallback(() => {
    resetView();
    onIndexChange(currentIndex < images.length - 1 ? currentIndex + 1 : 0);
  }, [currentIndex, images.length, onIndexChange, resetView]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && hasMultipleImages) {
        goToPrevious();
      } else if (e.key === "ArrowRight" && hasMultipleImages) {
        goToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, hasMultipleImages, goToPrevious, goToNext]);

  // 한 손가락 + 줌 없음일 때만 아래로 끌어 닫기. 두 손가락(핀치)·줌 상태는 제외.
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isZoomed || e.touches.length !== 1) {
        touchStartY.current = null;
        return;
      }
      touchStartY.current = e.touches[0].clientY;
    },
    [isZoomed]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartY.current === null || isZoomed) return;
      if (e.touches.length !== 1) {
        touchStartY.current = null;
        setDragY(0);
        return;
      }
      const dy = e.touches[0].clientY - touchStartY.current;
      setDragY(dy > 0 ? dy : 0); // 아래 방향만
    },
    [isZoomed]
  );

  const handleTouchEnd = useCallback(() => {
    if (touchStartY.current === null) return;
    if (dragY > SWIPE_CLOSE_THRESHOLD) {
      handleClose();
      return;
    }
    setDragY(0);
    touchStartY.current = null;
  }, [dragY, handleClose]);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 overflow-hidden focus:outline-none"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <VisuallyHidden.Root>
            <DialogTitle>{imageViewer}</DialogTitle>
          </VisuallyHidden.Root>

          {/* Image — 핀치/더블탭 줌은 이 이미지에만 적용. swipe-down 으로 닫기. */}
          <div
            className="absolute inset-0 z-0 pt-[max(1rem,var(--loc-safe-area-inset-top))] pb-[max(1rem,var(--loc-safe-area-inset-bottom))]"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              transform: dragY ? `translateY(${dragY}px)` : undefined,
              opacity: dragY ? Math.max(0.3, 1 - dragY / 500) : 1,
              transition: dragY
                ? "none"
                : "transform 0.2s ease-out, opacity 0.2s ease-out",
            }}
          >
            <TransformWrapper
              key={currentIndex}
              minScale={1}
              maxScale={4}
              centerOnInit
              doubleClick={{ mode: "toggle", step: 2 }}
              panning={{ disabled: !isZoomed }}
              onZoomStop={(ref) => setIsZoomed(ref.state.scale > 1.01)}
              onPinchStop={(ref) => setIsZoomed(ref.state.scale > 1.01)}
            >
              <TransformComponent
                wrapperClass="!w-full !h-full"
                contentClass="!w-full !h-full !flex !items-center !justify-center"
              >
                {/* next/image 의 fill 은 transform 컨테이너 안에서 다루기 까다로워
                    줌 뷰어에서는 plain img 사용(Supabase URL 이라 어차피 unoptimized) */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={currentImage.url}
                  alt={currentImage.name}
                  className="max-w-full max-h-full object-contain select-none"
                  draggable={false}
                />
              </TransformComponent>
            </TransformWrapper>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className={`absolute ${TOP_SAFE} right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors`}
            aria-label={closeImage}
          >
            <X className="h-6 w-6" />
          </button>

          {/* Image counter */}
          {hasMultipleImages && (
            <div
              className={`absolute ${TOP_SAFE} left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm`}
            >
              {currentIndex + 1} / {images.length}
            </div>
          )}

          {/* Previous button */}
          {hasMultipleImages && (
            <button
              onClick={goToPrevious}
              className="absolute top-1/2 -translate-y-1/2 left-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              aria-label={previousImage}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          {/* Next button */}
          {hasMultipleImages && (
            <button
              onClick={goToNext}
              className="absolute top-1/2 -translate-y-1/2 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              aria-label={nextImage}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
